import React, { useEffect, useRef, useState } from 'react'
import { IItemProps } from '@aytm/app/swidget/base/questions/Ecommerce/types.ts'
import Carousel from '@aytm/app/swidget/base/questions/Ecommerce/components/Carousel.tsx'
import AssetsModal from './ViewComponents/AssetsModal.tsx'
import { cleanBrandTitle } from '@aytm/app/swidget/base/questions/Ecommerce/utils/cleanBrandTitle.ts'
import { formatPriceWithLocale } from '@aytm/app/swidget/base/questions/Ecommerce/utils/formatPriceWithLocale.ts'
import { replaceTags } from '@aytm/app/swidget/base/questions/Ecommerce/utils/replaceTags.ts'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport.ts'
import CardHeader from './mobile/CardHeader.tsx'
import EmptyLink from '@aytm/app/swidget/base/components/EmptyLink.tsx'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'
import Rating from './components/Rating.tsx'
import { unescapeTags } from '@aytm/lib/common/func.js'
import Gallery from './components/Gallery.tsx'
import useTimeline from '../../../modules/timeline/useTimeline.ts'
import commonStyle from '@aytm/app/swidget/base/questions/Ecommerce/Amazon/common.module.scss'
import style from './amazon.module.scss'
import Subproducts from './ViewComponents/Subproducts.tsx'
import DeliveryPanel from './DeliveryPanel.tsx'
import ItemPreview from './ViewComponents/ItemPreview.tsx'
import SubscribeCoupon from './SubscribeCoupon.tsx'
import previewStyle from './ViewComponents/ItemPreview.module.scss'
import TrendingRow from './ViewComponents/TrendingRow.tsx'
import SponsoredLabel from './ViewComponents/SponsoredLabel.tsx'
import ecoLeafIcon from './img/ecoLeaf.png'

const Item = ({
  schema,
  questionSchema,
  response,
  qty,
  onClose,
  onAddToCart,
  onBuyNow,
  findability,
  scrollTop,
  onItemSelect,
  rightColumnProductIndex = -1,
  onBannerClick,
  onProductAction,
  onSpin,
  saveScrollPoint
}: IItemProps) => {
  const timeline = useTimeline()
  const [modalState, setModalState] = useState<{ open: boolean; tab: number; assetActiveIndex: number }>({
    open: false,
    tab: null,
    assetActiveIndex: null
  })
  const randomInDate = useRef<Date>(null)
  const randomDate = useRef<Date>(null)
  const viewport = useViewport()
  const { t, languageTag } = useSurveyLocale()
  const productRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (productRef.current) {
      productRef.current.focus()
    }
    scrollTop()
  }, [scrollTop])

  const onSubitemSelect = (index: number) => {
    timeline.log({
      ref: schema.id,
      type: 'close_popup'
    })
    onItemSelect(index)
  }

  const onCloseAssetsModel = (currentTab: number) => {
    timeline.log({
      ref: schema.id,
      type: 0 === currentTab ? 'close_video_dialog' : 'close_image_dialog'
    })
    setModalState({ open: false, tab: null, assetActiveIndex: null })
  }
  const onOpenAssetsModal = (assetActiveIndex: number, tab: number) => {
    setModalState({ open: true, tab, assetActiveIndex })
    timeline.log({
      ref: schema.id,
      type: 0 === tab ? 'open_video_dialog' : 'open_image_dialog'
    })
  }

  if (!randomInDate.current || !randomDate.current) {
    randomInDate.current = new Date()
    if (schema.prime) randomInDate.current.setDate(randomInDate.current.getDate() + schema.prime - 1)
    else {
      randomDate.current = new Date()
      randomDate.current.setDate(randomDate.current.getDate() + ((schema.text?.length || 0) % 2) + 1)
      randomInDate.current = new Date(randomDate.current)
      randomInDate.current.setDate(randomInDate.current.getDate() + ((schema.description?.length || 0) % 3) + 1)
    }
  }
  const percent = Number((((schema.price - schema.discounted_price) / schema.price) * 100).toFixed(0))
  // schema allows dimensions to be nil, and unvalidated entries can miss name/value or hold non-strings
  const dimensions = (schema.dimensions || []).filter(dimension =>
    [dimension?.name, dimension?.value].every(v => typeof v === 'string' && v)
  )

  return (
    <div
      className={style.product}
      tabIndex={0}
      aria-label={t('questions', 'ec', 'amazon', 'productLabel')}
      ref={productRef}
    >
      {modalState.open && (
        <AssetsModal
          schema={schema}
          assetActiveIndex={modalState.assetActiveIndex}
          tab={modalState.tab}
          onCloseAssetsModel={currentTab => onCloseAssetsModel(currentTab)}
        />
      )}
      <button
        type="button"
        className={style.backToResults}
        onClick={onClose}
        dangerouslySetInnerHTML={{
          __html: t('questions', 'ec', 'amazon', 'backToResults')
        }}
      />
      {(schema.sponsored || schema.featured) && (
        <div className={style.sponsored}>
          <span className={style.sponsoredText}>{t('questions', 'ec', 'amazon', 'sponsored')}</span>
          <EmptyLink>
            <div className={style.sponsoredIcon} />
          </EmptyLink>
        </div>
      )}
      {viewport.mobile && <CardHeader schema={schema} />}

      <div className={style.content}>
        {schema.image && schema.image.original_url ? (
          <div className={style.gallery}>
            <>
              {viewport.mobile ? (
                <Carousel key={schema.id} schema={schema} store="amazon" pagination="amazon" />
              ) : (
                <>
                  <Gallery
                    key={schema.id}
                    magnifierClass={style.magnifier}
                    emulatedMobile={viewport.emulated}
                    schema={schema}
                    externalStyle={'amazon'}
                    onOpenAssetsModal={(assetActiveIndex: number, tab: number) =>
                      onOpenAssetsModal(assetActiveIndex, tab)
                    }
                  />
                  <span>{t('questions', 'ec', 'amazon', 'roll')}</span>
                </>
              )}
            </>
          </div>
        ) : (
          <div className={commonStyle.emptyImage} style={{ minHeight: 300 }} />
        )}

        {!viewport.mobile && (
          <div className={style.center}>
            <span className={style.title} dangerouslySetInnerHTML={{ __html: schema.text }} />
            <div className={`${style.link}`}>
              <EmptyLink>
                <span>{unescapeTags(schema.sub_title)}</span>
              </EmptyLink>
            </div>
            <div>
              <span className={`${commonStyle.stars} ${style.starsBox}`}>
                <div className={commonStyle.numericRating}>{schema.rating || 0}</div>
                <EmptyLink>
                  <Rating rating={schema.rating || 0} size="small" />
                </EmptyLink>
                <EmptyLink>
                  <span className={`${commonStyle.arrowDown} ${commonStyle.link}`}>
                    <i />
                    <span>
                      {schema.reviews} {t('questions', 'ec', 'amazon', 'ratings')}
                    </span>
                  </span>
                </EmptyLink>

                <span className={style.askPipe}>|</span>
                <EmptyLink>
                  <span className={`${commonStyle.arrowDown} ${commonStyle.link}`}>
                    <span>{t('questions', 'ec', 'amazon', 'searchPage')}</span>
                  </span>
                </EmptyLink>
              </span>
              {schema.amazon_choice && (
                <div className={style.labelsContainer}>
                  <span className={commonStyle.amazonChoiceBadge}>
                    {t('questions', 'ec', 'amazon', 'amazonsChoice')}
                  </span>
                </div>
              )}
            </div>
            {schema.green_label ? (
              <div className={style.labels}>
                <span className={style.greenLabel} dangerouslySetInnerHTML={{ __html: schema.green_label }} />
              </div>
            ) : (
              ''
            )}
            {schema.eco_label ? (
              <EmptyLink>
                <span className={commonStyle.ecoLabel}>
                  <img src={ecoLeafIcon} width={18} height={18} alt="" />
                  <span className={commonStyle.arrowDown}>
                    <span dangerouslySetInnerHTML={{ __html: schema.eco_label }} /> <i />
                  </span>
                </span>
              </EmptyLink>
            ) : (
              ''
            )}
            <span className={style.bought}>
              <b>{unescapeTags(schema.bought)}</b>
            </span>
            <div className={style.divider} />
            {schema.red_label ? (
              <div className={style.labels}>
                <span className={style.redLabel} dangerouslySetInnerHTML={{ __html: schema.red_label }} />
              </div>
            ) : (
              ''
            )}
            <div className={style.container}>
              {schema.discounted_price && schema.price && percent > 0 ? (
                <div className={style.percent}>-{percent}%</div>
              ) : (
                ''
              )}
              <div className={style.price}>
                <span
                  className={style.bigPrice}
                  dangerouslySetInnerHTML={{
                    __html: formatPriceWithLocale(
                      schema.discounted_price ? schema.discounted_price : schema.price,
                      questionSchema.price_mask,
                      languageTag,
                      true,
                      true
                    )
                  }}
                ></span>
              </div>
              {schema.per_count_price ? (
                <span className={style.itemsPerPrice}>({unescapeTags(schema.per_count_price)})</span>
              ) : (
                ''
              )}
            </div>
            <div className={style.price}>
              {schema.discounted_price && schema.price > schema.discounted_price ? (
                <span className={style.smallPrice}>
                  {t('questions', 'ec', 'amazon', 'listPrice')} &nbsp;
                  <span
                    dangerouslySetInnerHTML={{
                      __html: formatPriceWithLocale(schema.price, questionSchema.price_mask, languageTag, true)
                    }}
                  />
                </span>
              ) : (
                ''
              )}
            </div>
            {schema.prime ? <i className={`${commonStyle['prime' + schema.prime]} ${commonStyle.primeLabel}`} /> : ''}

            <EmptyLink>
              <span className={`${style.small} ${style.link} ${style.getGiftLink}`}>
                {t('questions', 'ec', 'amazon', 'getGift')}{' '}
              </span>
            </EmptyLink>

            <EmptyLink>
              <span className={style.base}>
                <span
                  dangerouslySetInnerHTML={{
                    __html: t('questions', 'ec', 'amazon', 'getShip')
                  }}
                />
                <span className={commonStyle.link}>{t('questions', 'ec', 'amazon', 'prime')}</span>
              </span>
            </EmptyLink>
            {questionSchema.max_qty_per_product > 0 ? (
              <span className={style.leftInStock}>
                {t<(left: number) => string>(
                  'questions',
                  'ec',
                  'amazon',
                  'leftInStock'
                )(questionSchema.max_qty_per_product)}
              </span>
            ) : (
              ''
            )}
            <EmptyLink>
              <span className={`${commonStyle.arrowDown} ${commonStyle.link} ${style.freeReturns}`}>
                <span>{t('questions', 'ec', 'amazon', 'freeReturns')}</span>&nbsp;&nbsp;
                <i />
              </span>
            </EmptyLink>
            {schema.subscribe_coupon_percent ? (
              <SubscribeCoupon couponPercent={schema.subscribe_coupon_percent} />
            ) : null}
            <Subproducts questionSchema={questionSchema} schema={schema} onSubitemSelect={onSubitemSelect} />

            <div className={style.divider} />
            <div className={style.infoTitleContainer}>
              <span className={style.infoTitle}>{t('questions', 'ec', 'amazon', 'topHighlights')}</span>
              <EmptyLink>
                <i className="pi pi-chevron-up" />
              </EmptyLink>
            </div>
            <div className={style.infoTable} data-testid="SW-SH-TopHighlights">
              {schema.sub_title && (
                <div className={style.infoTableRow}>
                  <div className={style.tableTitleCell}>{t('questions', 'ec', 'amazon', 'brand')}</div>
                  <div className={style.tableValueCell}>
                    {cleanBrandTitle(unescapeTags(schema.sub_title) as string)}
                  </div>
                </div>
              )}
              {dimensions.length > 0 && (
                <>
                  {dimensions.map(({ name, value }, index) => (
                    <div className={style.infoTableRow} key={index}>
                      <div className={style.tableTitleCell}>{name}</div>
                      <div className={style.tableValueCell}>{value}</div>
                    </div>
                  ))}
                </>
              )}
              {schema.sub_title || dimensions.length > 0 ? <div className={style.divider} /> : ''}
            </div>
            {schema.description && (
              <>
                <span className={style.about}>{t('questions', 'ec', 'amazon', 'aboutItem')}</span>
                <span
                  className={style.base}
                  dangerouslySetInnerHTML={{
                    __html: replaceTags(unescapeTags(schema.description) as string)
                  }}
                />
              </>
            )}
            {schema.feature_bullets && (
              <>
                <div className={style.divider} />
                <div className={style.infoTitleContainer}>
                  <span className={style.infoTitle}>{t('questions', 'ec', 'amazon', 'itemDetails')}</span>
                  <EmptyLink>
                    <i className="pi pi-chevron-up" />
                  </EmptyLink>
                </div>
                <span className={style.about}>{t('questions', 'ec', 'amazon', 'bullets')}</span>
                <span
                  className={style.base}
                  dangerouslySetInnerHTML={{
                    __html: replaceTags(unescapeTags(schema.feature_bullets) as string)
                  }}
                />
              </>
            )}
            <div className={style.divider} />
            <div className={style.infoTitleContainer}>
              <span className={style.infoTitle}>{t('questions', 'ec', 'amazon', 'measurements')}</span>
              <EmptyLink>
                <i className="pi pi-chevron-down" />
              </EmptyLink>
            </div>
            <div className={style.divider} />
            <EmptyLink>
              <div className={style.productSpecs}>{t('questions', 'ec', 'amazon', 'seeAllSpecs')}</div>
            </EmptyLink>
          </div>
        )}
        <div className={style.right}>
          {viewport.mobile && (
            <Subproducts questionSchema={questionSchema} schema={schema} onSubitemSelect={onSubitemSelect} />
          )}

          {/* <div className={style.prime}>
            <img
              src="https://m.media-amazon.com/images/G/01/marketing/prime/new_prime_logo_RGB_blue._CB426090081_.png"
              alt="Amazon prime logo"
              className="prime-bbop-logo"
            />
            <span className={style.base}>
              <b>Enjoy fast, free delivery, exclusive deals, and award-winning movies & TV shows with Prime</b>
            </span>
            <span className={style.base}>
              Try Prime and start saving today with&nbsp;
              <b>fast, free delivery</b>
            </span>
          </div> */}
          {/* {viewport.mobile && (
            <div className={style.primeMobileCoxntainer}>
              <img
                src="https://m.media-amazon.com/images/G/01/marketing/prime/new_prime_logo_RGB_blue._CB426090081_.png"
                alt="Amazon prime logo"
                className="prime-bbop-logo"
              />
              <div className={style.textItem}>
                <p>Unlock fast, free delivery on millions of Prime eligible items.</p>
                <button>Join Prime</button>
              </div>
            </div>
          )} */}
          <DeliveryPanel
            schema={schema}
            questionSchema={questionSchema}
            qty={qty}
            findability={findability}
            randomInDate={randomInDate.current}
            randomDate={randomDate.current}
            onAddToCart={onAddToCart}
            onBuyNow={onBuyNow}
          />
          {rightColumnProductIndex !== -1 && (
            <>
              <ItemPreview
                schema={questionSchema.answers[rightColumnProductIndex]}
                questionSchema={questionSchema}
                onItemSelect={() => onBannerClick?.()}
                response={response.answers[rightColumnProductIndex]}
                className={previewStyle.banner}
                saveScrollPoint={() => {}}
                bannerPlacement="product_page"
                bannerSource={schema.id}
              />
              <div className={previewStyle.sponsoredRight}>
                <SponsoredLabel />
              </div>
            </>
          )}
        </div>
      </div>
      {onProductAction && onSpin && onItemSelect && saveScrollPoint && (
        <TrendingRow
          schema={questionSchema}
          response={response}
          ceilWidth={160}
          productsPerRow={5}
          bannerPlacement="footer"
          filterKey="footer"
          bannerSource={schema.id}
          onProductAction={onProductAction}
          onSpin={onSpin}
          onItemSelect={onItemSelect}
          saveScrollPoint={saveScrollPoint}
        />
      )}
    </div>
  )
}

export default Item
